/**
 * Lead Journey Dialog — shows full tracking journey for a lead
 * (sessions, touchpoints, events, attribution)
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { displayablePhone } from "@/lib/displayable-phone";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2,
  ExternalLink,
  Clock,
  MousePointerClick,
  ShoppingCart,
  Phone,
  Mail,
  Globe,
  ArrowRight,
  CalendarCheck,
  Eye,
  Target,
  Smartphone,
  Monitor,
  MapPin,
  ChevronDown,
  ChevronRight,
  Tag,
  Plus,
  Trash2,
  Route,
  Megaphone,
} from "lucide-react";
import { AdPreviewContent } from "@/components/ad-preview-content";

interface GhlJourneyData {
  contact: {
    id: string;
    name: string;
    email: string;
    phone: string;
    source: string;
    createdAt: string;
    tags: string[];
  };
  attributions: Array<{
    medium: string;
    source: string;
    url: string;
    pageVisitType: string;
    parentName: string;
    timestamp: string;
  }>;
  appointments: Array<{ id: string; title: string; status: string; startTime: string }>;
  opportunities: Array<{
    id: string;
    name: string;
    status: string;
    monetaryValue: number;
    createdAt: string;
  }>;
}

interface JourneyData {
  found: boolean;
  journey: {
    visitor: {
      id: string;
      trackingId: string;
      email: string | null;
      phone: string | null;
      name: string | null;
      firstUtmSource: string | null;
      firstUtmMedium: string | null;
      firstUtmCampaign: string | null;
      firstReferrer: string | null;
      firstLandingPage: string | null;
      firstSeenAt: string | null;
      lastUtmSource: string | null;
      lastUtmMedium: string | null;
      lastUtmCampaign: string | null;
      lastSeenAt: string | null;
      ghlContactId: string | null;
    };
    sessions: Array<{
      id: string;
      sessionId: string;
      utmSource: string | null;
      utmMedium: string | null;
      utmCampaign: string | null;
      landingPage: string | null;
      referrer: string | null;
      startedAt: string;
    }>;
    events: Array<{
      id: string;
      eventId: string;
      eventName: string;
      sessionId: string;
      pageUrl: string | null;
      utmSource: string | null;
      utmCampaign: string | null;
      email: string | null;
      phone: string | null;
      orderId: string | null;
      revenue: string | null;
      currency: string | null;
      serverTimestamp: string;
      metadata: Record<string, any> | null;
    }>;
    conversions: Array<{
      id: string;
      conversionType: string;
      email: string | null;
      phone: string | null;
      orderId: string | null;
      revenue: string | null;
      currency: string | null;
      firstUtmSource: string | null;
      firstUtmCampaign: string | null;
      lastUtmSource: string | null;
      lastUtmCampaign: string | null;
      lastNonDirectUtmSource: string | null;
      lastNonDirectUtmCampaign: string | null;
      convertedAt: string;
      metadata: Record<string, any> | null;
    }>;
    touchpoints: Array<{
      id: string;
      channel: string | null;
      source: string | null;
      medium: string | null;
      campaign: string | null;
      referrer: string | null;
      landingPage: string | null;
      isDirect: boolean;
      capturedAt: string;
    }>;
    identities: Array<{
      identityType: string;
      identityValue: string;
    }>;
  } | null;
  ghlJourney?: GhlJourneyData | null;
}

const EVENT_INFO: Record<string, { icon: typeof Eye; color: string; labelKey: string }> = {
  page_view: { icon: Eye, color: "text-white/40", labelKey: "leadJourney.events.page_view" },
  view_content: { icon: Eye, color: "text-blue-400", labelKey: "leadJourney.events.view_content" },
  lead: { icon: Target, color: "text-yellow-400", labelKey: "leadJourney.events.lead" },
  booked_call: {
    icon: CalendarCheck,
    color: "text-blue-400",
    labelKey: "leadJourney.events.booked_call",
  },
  qualified_call: {
    icon: Phone,
    color: "text-emerald-400",
    labelKey: "leadJourney.events.qualified_call",
  },
  checkout_started: {
    icon: ShoppingCart,
    color: "text-orange-400",
    labelKey: "leadJourney.events.checkout_started",
  },
  purchase: {
    icon: ShoppingCart,
    color: "text-emerald-400",
    labelKey: "leadJourney.events.purchase",
  },
  upsell: { icon: ShoppingCart, color: "text-purple-400", labelKey: "leadJourney.events.upsell" },
  refund: { icon: ShoppingCart, color: "text-red-400", labelKey: "leadJourney.events.refund" },
};

const CHANNEL_INFO: Record<string, { labelKey: string; color: string }> = {
  paid_social: {
    labelKey: "leadJourney.channels.paid_social",
    color: "bg-blue-500/20 text-blue-400",
  },
  paid_search: {
    labelKey: "leadJourney.channels.paid_search",
    color: "bg-green-500/20 text-green-400",
  },
  organic_search: {
    labelKey: "leadJourney.channels.organic_search",
    color: "bg-emerald-500/20 text-emerald-400",
  },
  organic_social: {
    labelKey: "leadJourney.channels.organic_social",
    color: "bg-purple-500/20 text-purple-400",
  },
  direct: { labelKey: "leadJourney.channels.direct", color: "bg-white/10 text-white/60" },
  referral: {
    labelKey: "leadJourney.channels.referral",
    color: "bg-orange-500/20 text-orange-400",
  },
  email: { labelKey: "leadJourney.channels.email", color: "bg-yellow-500/20 text-yellow-400" },
  other: { labelKey: "leadJourney.channels.other", color: "bg-white/10 text-white/40" },
};

function formatDate(dateStr: string, locale: string) {
  return new Date(dateStr).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Show the EXACT page: host + path + query (drop only the protocol). Rendered
// with break-all so it is never visually cut — the user needs the full link.
function shortenUrl(url: string | null): string {
  if (!url) return "—";
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

// Friendly channel label from utm source/medium (+ referrer fallback), so the
// journey says "Meta Ads" / "YouTube Organic" / "Email" instead of raw "facebook"
// or a useless "Referral". Mirrors classifyChannel from the Sales Data page.
const JOURNEY_PAID_CHANNELS: Record<string, string> = {
  facebook: "Meta Ads",
  fb: "Meta Ads",
  meta: "Meta Ads",
  instagram: "Instagram Ads",
  ig: "Instagram Ads",
  google: "Google Ads",
  tiktok: "TikTok Ads",
  youtube: "YouTube Ads",
  linkedin: "LinkedIn Ads",
  bing: "Bing Ads",
};
const JOURNEY_PAID_MEDIUMS = new Set([
  "cpc",
  "ppc",
  "paid",
  "paidsocial",
  "paid_social",
  "paid-social",
  "cpm",
  "display",
  "ads",
]);

function channelLabel(
  source?: string | null,
  medium?: string | null,
  referrer?: string | null
): string {
  const s = (source || "").toLowerCase();
  const m = (medium || "").toLowerCase();
  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
  if (s) {
    if (m && JOURNEY_PAID_MEDIUMS.has(m)) return JOURNEY_PAID_CHANNELS[s] ?? `${cap(s)} Ads`;
    if (m === "social" || m === "organic_social") return `${cap(s)} Organic`;
    if (s === "email" || s === "newsletter" || m === "email") return "Email";
    if (m === "organic") return "Organic Search";
    if (m === "referral") return `Referral: ${source}`;
    if (JOURNEY_PAID_CHANNELS[s]) return JOURNEY_PAID_CHANNELS[s];
    return `${source}${medium ? ` / ${medium}` : ""}`;
  }
  if (referrer) {
    try {
      const h = new URL(referrer).hostname.replace(/^www\./, "");
      if (/google\.|bing\.|duckduckgo|yahoo|ecosia/.test(h)) return "Organic Search";
      if (h.includes("facebook")) return "Facebook Organic";
      if (h.includes("instagram")) return "Instagram Organic";
      if (h.includes("youtube")) return "YouTube Organic";
      if (h.includes("tiktok")) return "TikTok Organic";
      return h;
    } catch {
      return referrer;
    }
  }
  return "Direct / Webhook";
}

const PAGE_VISIT_INFO: Record<string, { icon: typeof Eye; color: string; labelKey: string }> = {
  form: { icon: Target, color: "text-yellow-400", labelKey: "leadJourney.pageVisit.form" },
  funnel: { icon: Eye, color: "text-blue-400", labelKey: "leadJourney.pageVisit.funnel" },
  survey: { icon: Eye, color: "text-purple-400", labelKey: "leadJourney.pageVisit.survey" },
  website: { icon: Globe, color: "text-white/50", labelKey: "leadJourney.pageVisit.website" },
};

const APPT_STATUS_INFO: Record<string, { color: string; labelKey: string }> = {
  confirmed: { color: "text-blue-400", labelKey: "leadJourney.apptStatus.confirmed" },
  showed: { color: "text-emerald-400", labelKey: "leadJourney.apptStatus.showed" },
  noshow: { color: "text-red-400", labelKey: "leadJourney.apptStatus.noshow" },
  cancelled: { color: "text-white/40", labelKey: "leadJourney.apptStatus.cancelled" },
  invalid: { color: "text-white/30", labelKey: "leadJourney.apptStatus.invalid" },
};

/**
 * Shown when neither pixel nor GHL data is available.
 * Explains the two possible reasons and how to fix each.
 */
function NoJourneyDataMessage() {
  const { t } = useTranslation();
  return (
    <div className="py-8 px-4 space-y-5">
      <div className="text-center">
        <MousePointerClick className="w-8 h-8 text-white/15 mx-auto mb-2" />
        <p className="text-white/50 text-sm font-medium">{t("leadJourney.empty.title")}</p>
      </div>

      <div className="space-y-3 max-w-md mx-auto">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-400 text-[10px] font-bold">1</span>
            </div>
            <div>
              <p className="text-white/60 text-xs font-medium">
                {t("leadJourney.empty.reason1Title")}
              </p>
              <p className="text-white/30 text-[11px] mt-0.5">
                {t("leadJourney.empty.reason1Desc")}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-orange-400 text-[10px] font-bold">2</span>
            </div>
            <div>
              <p className="text-white/60 text-xs font-medium">
                {t("leadJourney.empty.reason2Title")}
              </p>
              <p className="text-white/30 text-[11px] mt-0.5">
                {t("leadJourney.empty.reason2Desc")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a GHL-based journey when no pixel tracking data exists.
 * Shows contact creation, page visits/form submissions, appointments, and opportunities.
 */
function GhlJourneyView({ ghlJourney }: { ghlJourney: GhlJourneyData }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "pl" ? "pl" : "en";
  const { contact, attributions, appointments, opportunities } = ghlJourney;

  // Build a unified timeline sorted by date
  type TimelineItem =
    | { type: "created"; date: string; source: string }
    | { type: "attribution"; date: string; data: GhlJourneyData["attributions"][0] }
    | { type: "appointment"; date: string; data: GhlJourneyData["appointments"][0] }
    | { type: "opportunity"; date: string; data: GhlJourneyData["opportunities"][0] };

  const timeline: TimelineItem[] = [];

  if (contact.createdAt) {
    timeline.push({ type: "created", date: contact.createdAt, source: contact.source });
  }

  for (const attr of attributions) {
    if (attr.timestamp) {
      timeline.push({ type: "attribution", date: attr.timestamp, data: attr });
    }
  }

  for (const appt of appointments) {
    if (appt.startTime) {
      timeline.push({ type: "appointment", date: appt.startTime, data: appt });
    }
  }

  for (const opp of opportunities) {
    if (opp.createdAt) {
      timeline.push({ type: "opportunity", date: opp.createdAt, data: opp });
    }
  }

  timeline.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <div className="space-y-4">
      {/* Header — source badge */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white/80">{t("leadJourney.ghl.profile")}</h3>
          <span className="text-[10px] px-2 py-0.5 rounded bg-orange-500/20 text-orange-400">
            {t("leadJourney.ghl.fromCrm")}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {contact.email && (
            <div className="flex items-center gap-1.5 text-white/50">
              <Mail className="w-3 h-3" />
              <span className="truncate">{contact.email}</span>
            </div>
          )}
          {contact.phone && (
            <div className="flex items-center gap-1.5 text-white/50">
              <Phone className="w-3 h-3" />
              <span>{contact.phone}</span>
            </div>
          )}
          {contact.createdAt && (
            <div className="flex items-center gap-1.5 text-white/50">
              <Clock className="w-3 h-3" />
              <span>
                {t("leadJourney.ghl.createdAt")} {formatDate(contact.createdAt, dateLocale)}
              </span>
            </div>
          )}
          {contact.source && (
            <div className="flex items-center gap-1.5 text-white/50">
              <Globe className="w-3 h-3" />
              <span>
                {t("leadJourney.ghl.source")}: {contact.source}
              </span>
            </div>
          )}
        </div>
        {contact.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {contact.tags.map((tag, i) => (
              <span
                key={i}
                className="text-[10px] px-2 py-0.5 rounded bg-white/[0.06] text-white/40"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-white/80">{t("leadJourney.ghl.timeline")}</h3>
        {timeline.length === 0 ? (
          <p className="text-xs text-white/30 py-2">{t("leadJourney.ghl.noEvents")}</p>
        ) : (
          <div className="relative pl-4 border-l border-white/[0.08] space-y-3">
            {timeline.map((item, i) => {
              if (item.type === "created") {
                return (
                  <TimelineEntry
                    key={`created-${i}`}
                    date={item.date}
                    dotColor="bg-blue-500"
                    dateLocale={dateLocale}
                  >
                    <span className="text-white/70 font-medium">
                      {t("leadJourney.ghl.contactCreated")}
                    </span>
                    {item.source && (
                      <span className="text-white/40 ml-2">
                        {t("leadJourney.ghl.source")}: {item.source}
                      </span>
                    )}
                  </TimelineEntry>
                );
              }
              if (item.type === "attribution") {
                const info = PAGE_VISIT_INFO[item.data.pageVisitType] || PAGE_VISIT_INFO.website;
                const Icon = info.icon;
                return (
                  <TimelineEntry
                    key={`attr-${i}`}
                    date={item.date}
                    dotColor="bg-white/30"
                    dateLocale={dateLocale}
                  >
                    <Icon className={`w-3 h-3 ${info.color} inline mr-1`} />
                    <span className={info.color}>{t(info.labelKey)}</span>
                    {item.data.parentName && (
                      <span className="text-white/40 ml-1.5">{item.data.parentName}</span>
                    )}
                    {item.data.source && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.05] text-white/30 ml-1.5">
                        {item.data.medium || item.data.source}
                      </span>
                    )}
                  </TimelineEntry>
                );
              }
              if (item.type === "appointment") {
                const status = APPT_STATUS_INFO[item.data.status] || {
                  color: "text-white/50",
                  labelKey: "",
                };
                return (
                  <TimelineEntry
                    key={`appt-${item.data.id}`}
                    date={item.date}
                    dotColor="bg-blue-400"
                    dateLocale={dateLocale}
                  >
                    <CalendarCheck className="w-3 h-3 text-blue-400 inline mr-1" />
                    <span className="text-blue-400 font-medium">
                      {t("leadJourney.ghl.meeting")}
                    </span>
                    <span className="text-white/40 ml-1.5">{item.data.title}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded bg-white/[0.05] ml-1.5 ${status.color}`}
                    >
                      {status.labelKey ? t(status.labelKey) : item.data.status}
                    </span>
                  </TimelineEntry>
                );
              }
              if (item.type === "opportunity") {
                const isWon = item.data.status === "won";
                const dotColor = isWon ? "bg-emerald-400" : "bg-orange-400";
                return (
                  <TimelineEntry
                    key={`opp-${item.data.id}`}
                    date={item.date}
                    dotColor={dotColor}
                    dateLocale={dateLocale}
                  >
                    <ShoppingCart
                      className={`w-3 h-3 ${isWon ? "text-emerald-400" : "text-orange-400"} inline mr-1`}
                    />
                    <span
                      className={
                        isWon ? "text-emerald-400 font-medium" : "text-orange-400 font-medium"
                      }
                    >
                      {item.data.name || t("leadJourney.ghl.opportunity")}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.05] text-white/40 ml-1.5">
                      {item.data.status}
                    </span>
                    {item.data.monetaryValue > 0 && (
                      <span className="text-emerald-400 font-medium ml-1.5">
                        {item.data.monetaryValue.toLocaleString(dateLocale)}{" "}
                        {t("leadJourney.ghl.currency")}
                      </span>
                    )}
                  </TimelineEntry>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineEntry({
  date,
  dotColor,
  dateLocale,
  children,
}: {
  date: string;
  dotColor: string;
  dateLocale: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex items-start gap-2 text-xs">
      <div className={`absolute -left-[21px] top-1 w-2 h-2 rounded-full ${dotColor}`} />
      <div className="flex-1 flex items-center flex-wrap gap-1">{children}</div>
      <span className="text-white/20 text-[10px] flex-shrink-0">
        {formatDate(date, dateLocale)}
      </span>
    </div>
  );
}

// Manual conversion types addable from the journey "Tagi" tab.
const MANUAL_CONV_TYPES: { value: string; label: string; hasValue: boolean }[] = [
  { value: "booked_call", label: "Rozmowa umówiona", hasValue: false },
  { value: "qualified_call", label: "Rozmowa sprzedażowa", hasValue: true },
  { value: "purchase", label: "Sprzedaż", hasValue: true },
  { value: "upsell", label: "Upsell", hasValue: true },
];

/** "Tagi / Konwersje" tab — shows first/last source + lets the user add/remove a
 *  call or a sale (with value + date) for this visitor. Writes to trackingConversions
 *  via /api/tracking/journey/conversion; agency-safe (owner-scoped server-side). */
function ConversionManager({
  visitorId,
  conversions,
  firstLabel,
  lastLabel,
  forClientUserId,
  dateLocale,
  onChanged,
}: {
  visitorId: string;
  conversions: Array<{
    id: string;
    conversionType: string;
    revenue: string | null;
    currency: string | null;
    convertedAt: string;
  }>;
  firstLabel: string;
  lastLabel: string;
  forClientUserId?: string;
  dateLocale: string;
  onChanged: () => void;
}) {
  const [type, setType] = useState("purchase");
  const [value, setValue] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const selected = MANUAL_CONV_TYPES.find((m) => m.value === type);
  const typeLabel = (tp: string) => MANUAL_CONV_TYPES.find((m) => m.value === tp)?.label ?? tp;

  const add = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/tracking/journey/conversion", {
        visitorId,
        conversionType: type,
        revenue: selected?.hasValue && value ? Number(value.replace(",", ".")) : undefined,
        convertedAt: date ? new Date(`${date}T12:00:00`).toISOString() : undefined,
        forClientUserId: forClientUserId || undefined,
      }),
    onSuccess: () => {
      setValue("");
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest(
        "DELETE",
        `/api/tracking/journey/conversion?conversionId=${encodeURIComponent(id)}${forClientUserId ? `&forClientUserId=${encodeURIComponent(forClientUserId)}` : ""}`
      ),
    onSuccess: onChanged,
  });

  const manual = conversions.filter((c) =>
    ["booked_call", "qualified_call", "purchase", "upsell", "refund"].includes(c.conversionType)
  );

  return (
    <div className="space-y-4">
      {/* Źródło z atrybucji */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
          <div className="text-[10px] text-white/30 mb-1">Pierwsze źródło</div>
          <div className="text-sm font-medium text-emerald-300">{firstLabel}</div>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3">
          <div className="text-[10px] text-white/30 mb-1">Ostatnie źródło</div>
          <div className="text-sm font-medium text-sky-300">{lastLabel}</div>
        </div>
      </div>

      {/* Aktualne konwersje + usuwanie */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3 space-y-2">
        <h3 className="text-sm font-medium text-white/80">Konwersje</h3>
        {manual.length === 0 ? (
          <p className="text-xs text-white/35">Brak rozmów / sprzedaży. Dodaj poniżej.</p>
        ) : (
          <div className="space-y-1.5">
            {manual.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-2 text-xs bg-white/[0.02] rounded px-2 py-1.5"
              >
                <span className="text-white/70 font-medium">{typeLabel(c.conversionType)}</span>
                {c.revenue && (
                  <span className="text-emerald-400 font-medium">
                    {Number(c.revenue).toLocaleString(dateLocale)} {c.currency || "PLN"}
                  </span>
                )}
                <span className="text-white/30 ml-auto">
                  {formatDate(c.convertedAt, dateLocale)}
                </span>
                <button
                  type="button"
                  onClick={() => remove.mutate(c.id)}
                  disabled={remove.isPending}
                  className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  title="Usuń"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dodaj konwersję */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-3 space-y-2">
        <h3 className="text-sm font-medium text-white/80">Dodaj rozmowę / sprzedaż</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label className="text-[10px] text-white/40 block">Typ</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-8 px-2 text-xs bg-white/[0.06] border border-white/[0.12] rounded text-white focus:outline-none focus:border-white/[0.25]"
            >
              {MANUAL_CONV_TYPES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          {selected?.hasValue && (
            <div className="space-y-1">
              <label className="text-[10px] text-white/40 block">Wartość</label>
              <input
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="np. 4997"
                className="h-8 w-28 px-2 text-xs bg-white/[0.06] border border-white/[0.12] rounded text-white placeholder:text-white/25 focus:outline-none focus:border-white/[0.25]"
              />
            </div>
          )}
          <div className="space-y-1">
            <label className="text-[10px] text-white/40 block">Data</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-8 px-2 text-xs bg-white/[0.06] border border-white/[0.12] rounded text-white focus:outline-none focus:border-white/[0.25]"
            />
          </div>
          <button
            type="button"
            onClick={() => add.mutate()}
            disabled={add.isPending}
            className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 border border-emerald-500/30 disabled:opacity-50"
          >
            {add.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            Dodaj
          </button>
        </div>
        {add.isError && (
          <p className="text-[11px] text-red-300/80">Nie udało się dodać — spróbuj ponownie.</p>
        )}
      </div>
    </div>
  );
}

export default function LeadJourneyDialog({
  open,
  onClose,
  email,
  phone,
  name,
  ghlContactId,
  forClientUserId,
  adId,
  adName,
  adAccountId,
}: {
  open: boolean;
  onClose: () => void;
  email?: string;
  phone?: string;
  name?: string;
  ghlContactId?: string;
  forClientUserId?: string;
  adId?: string | null;
  adName?: string | null;
  adAccountId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "pl" ? "pl" : "en";
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery<JourneyData>({
    queryKey: [
      "/api/tracking/journey/by-ghl",
      { email, phone, ghl_contact_id: ghlContactId, forClientUserId },
    ],
    queryFn: async () => {
      console.log("[LeadJourney] Fetching journey for", {
        email,
        phone,
        ghlContactId,
        forClientUserId,
      });
      const result = await apiRequest("POST", "/api/tracking/journey/by-ghl", {
        email: email || undefined,
        phone: phone || undefined,
        ghl_contact_id: ghlContactId || undefined,
        forClientUserId: forClientUserId || undefined,
      });
      console.log("[LeadJourney] Response:", {
        found: result?.found,
        hasJourney: !!result?.journey,
        hasGhlJourney: !!result?.ghlJourney,
      });
      return result;
    },
    enabled: open && !!(email || phone || ghlContactId),
    staleTime: 1000 * 60 * 2,
  });

  const journey = data?.journey;
  const ghlJourney = data?.ghlJourney;
  const found = data?.found;

  // Debug logging
  if (!isLoading && data) {
    console.log("[LeadJourney] Render state:", {
      found,
      hasJourney: !!journey,
      hasGhlJourney: !!ghlJourney,
      error: error?.message,
    });
  }
  if (!isLoading && error) {
    console.error("[LeadJourney] Query error:", error);
  }

  const toggleSession = (sessionId: string) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const [tab, setTab] = useState<"path" | "tags" | "ad">("path");
  const refetchJourney = () =>
    queryClient.invalidateQueries({
      queryKey: [
        "/api/tracking/journey/by-ghl",
        { email, phone, ghl_contact_id: ghlContactId, forClientUserId },
      ],
    });

  // Group events by session
  const eventsBySession = new Map<string, NonNullable<JourneyData["journey"]>["events"]>();
  if (journey?.events) {
    for (const event of journey.events) {
      const key = event.sessionId ?? "__unknown";
      const list = eventsBySession.get(key) || [];
      list.push(event);
      eventsBySession.set(key, list);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] bg-[#0a0c10] border-white/[0.1] text-white overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-base">
            <MousePointerClick className="w-4 h-4 text-[#ef3e29]" />
            {t("leadJourney.title")}
          </DialogTitle>
          <div className="text-xs text-white/50">
            {/* VP-243: telefon jako etykieta tylko gdy jest numerem — inaczej pokazywał
                się tu bełkot z nazwy kampanii zapisany w polu telefonu. */}
            {name || email || displayablePhone(phone) || t("leadJourney.unknownUser")}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 min-h-0">
          {/* Tabs: Ścieżka | Tagi (manual conversions) | Reklama — zawsze widoczne, "Reklama"
              nie zależy od stanu zapytania o journey (VP-170). */}
          <div className="flex gap-1 border-b border-white/[0.08] mb-4">
            <button
              type="button"
              onClick={() => setTab("path")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === "path" ? "border-[#ef3e29] text-white" : "border-transparent text-white/45 hover:text-white/70"}`}
            >
              <Route className="w-3.5 h-3.5" /> Ścieżka
            </button>
            <button
              type="button"
              disabled={!journey}
              onClick={() => journey && setTab("tags")}
              title={!journey ? t("leadJourney.tagsUnavailable") : undefined}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                !journey
                  ? "border-transparent text-white/20 cursor-not-allowed"
                  : tab === "tags"
                    ? "border-[#ef3e29] text-white"
                    : "border-transparent text-white/45 hover:text-white/70"
              }`}
            >
              <Tag className="w-3.5 h-3.5" /> Tagi / Konwersje
            </button>
            <button
              type="button"
              onClick={() => setTab("ad")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === "ad" ? "border-[#ef3e29] text-white" : "border-transparent text-white/45 hover:text-white/70"}`}
            >
              <Megaphone className="w-3.5 h-3.5" /> Reklama
            </button>
          </div>

          {tab === "ad" ? (
            adId ? (
              <div className="space-y-3">
                {adName && <p className="text-sm text-white/70 truncate">{adName}</p>}
                <AdPreviewContent
                  adId={adId}
                  adAccountId={adAccountId ?? undefined}
                  forClientUserId={forClientUserId}
                />
              </div>
            ) : (
              <div className="text-center py-12">
                <Megaphone className="w-8 h-8 text-white/20 mx-auto mb-2" />
                <p className="text-white/40 text-sm">{t("leadJourney.noAdAttribution")}</p>
              </div>
            )
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 text-[#ef3e29] animate-spin" />
              <span className="ml-2 text-white/50 text-sm">{t("leadJourney.loading")}</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <MousePointerClick className="w-8 h-8 text-red-400/30 mx-auto mb-2" />
              <p className="text-white/40 text-sm">{t("leadJourney.refreshHint")}</p>
              <p className="text-white/25 text-xs mt-1 font-mono">{(error as Error).message}</p>
            </div>
          ) : !found && !ghlJourney ? (
            <NoJourneyDataMessage />
          ) : !journey && ghlJourney ? (
            <GhlJourneyView ghlJourney={ghlJourney} />
          ) : journey ? (
            <div className="space-y-4">
              {tab === "tags" ? (
                <ConversionManager
                  visitorId={journey.visitor.id}
                  conversions={journey.conversions}
                  firstLabel={channelLabel(
                    journey.visitor.firstUtmSource,
                    journey.visitor.firstUtmMedium,
                    journey.visitor.firstReferrer
                  )}
                  lastLabel={channelLabel(
                    journey.visitor.lastUtmSource,
                    journey.visitor.lastUtmMedium,
                    null
                  )}
                  forClientUserId={forClientUserId}
                  dateLocale={dateLocale}
                  onChanged={refetchJourney}
                />
              ) : (
                <>
                  {/* ─── Visitor Summary Card ─── */}
                  <div className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-white/80">
                        {t("leadJourney.profile")}
                      </h3>
                      <span className="text-[10px] text-white/30 font-mono">
                        {journey.visitor.trackingId}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      {journey.visitor.email && (
                        <div className="flex items-center gap-1.5 text-white/50">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{journey.visitor.email}</span>
                        </div>
                      )}
                      {journey.visitor.phone && (
                        <div className="flex items-center gap-1.5 text-white/50">
                          <Phone className="w-3 h-3" />
                          <span>{journey.visitor.phone}</span>
                        </div>
                      )}
                      {journey.visitor.firstSeenAt && (
                        <div className="flex items-center gap-1.5 text-white/50">
                          <Clock className="w-3 h-3" />
                          <span>
                            {t("leadJourney.firstSeen")}{" "}
                            {formatDate(journey.visitor.firstSeenAt, dateLocale)}
                          </span>
                        </div>
                      )}
                      {journey.visitor.lastSeenAt && (
                        <div className="flex items-center gap-1.5 text-white/50">
                          <Clock className="w-3 h-3" />
                          <span>
                            {t("leadJourney.lastSeen")}{" "}
                            {formatDate(journey.visitor.lastSeenAt, dateLocale)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* First / Last touch attribution — friendly channel label + raw
                    source/campaign + the EXACT entry page/referrer, so it's clear
                    whether the lead came from Meta ads, email, YouTube organic, etc. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="bg-white/[0.03] rounded p-2 space-y-1">
                        <div className="text-[10px] text-white/30">
                          {t("leadJourney.firstTouch")} (skąd przyszedł)
                        </div>
                        <div className="text-xs font-medium text-emerald-300">
                          {channelLabel(
                            journey.visitor.firstUtmSource,
                            journey.visitor.firstUtmMedium,
                            journey.visitor.firstReferrer
                          )}
                        </div>
                        {(journey.visitor.firstUtmSource || journey.visitor.firstUtmCampaign) && (
                          <div className="text-[10px] text-white/40">
                            {journey.visitor.firstUtmSource}
                            {journey.visitor.firstUtmMedium &&
                              ` / ${journey.visitor.firstUtmMedium}`}
                            {journey.visitor.firstUtmCampaign &&
                              ` / ${journey.visitor.firstUtmCampaign}`}
                          </div>
                        )}
                        {journey.visitor.firstLandingPage && (
                          <div
                            className="text-[10px] text-white/40 break-all"
                            title={journey.visitor.firstLandingPage}
                          >
                            <span className="text-white/25">strona wejścia:</span>{" "}
                            {shortenUrl(journey.visitor.firstLandingPage)}
                          </div>
                        )}
                        {journey.visitor.firstReferrer && (
                          <div
                            className="text-[10px] text-white/30 break-all"
                            title={journey.visitor.firstReferrer}
                          >
                            <span className="text-white/25">referrer:</span>{" "}
                            {journey.visitor.firstReferrer}
                          </div>
                        )}
                      </div>
                      <div className="bg-white/[0.03] rounded p-2 space-y-1">
                        <div className="text-[10px] text-white/30">
                          {t("leadJourney.lastTouch")} (ostatnie źródło)
                        </div>
                        <div className="text-xs font-medium text-sky-300">
                          {channelLabel(
                            journey.visitor.lastUtmSource,
                            journey.visitor.lastUtmMedium,
                            null
                          )}
                        </div>
                        {(journey.visitor.lastUtmSource || journey.visitor.lastUtmCampaign) && (
                          <div className="text-[10px] text-white/40">
                            {journey.visitor.lastUtmSource}
                            {journey.visitor.lastUtmMedium && ` / ${journey.visitor.lastUtmMedium}`}
                            {journey.visitor.lastUtmCampaign &&
                              ` / ${journey.visitor.lastUtmCampaign}`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ─── Conversions Summary ─── */}
                  {journey.conversions.length > 0 && (
                    <div className="bg-emerald-500/[0.05] border border-emerald-500/[0.15] rounded-lg p-3 space-y-2">
                      <h3 className="text-sm font-medium text-emerald-400">
                        {t("leadJourney.conversions")}
                      </h3>
                      <div className="space-y-1.5">
                        {journey.conversions.map((conv) => {
                          const info = EVENT_INFO[conv.conversionType] || EVENT_INFO.lead;
                          return (
                            <div key={conv.id} className="flex items-center gap-2 text-xs">
                              <info.icon className={`w-3.5 h-3.5 ${info.color}`} />
                              <span className="text-white/70 font-medium">{t(info.labelKey)}</span>
                              {conv.revenue && (
                                <span className="text-emerald-400 font-medium">
                                  {Number(conv.revenue).toLocaleString(dateLocale)}{" "}
                                  {conv.currency || "PLN"}
                                </span>
                              )}
                              <span className="text-white/30 ml-auto">
                                {formatDate(conv.convertedAt, dateLocale)}
                              </span>
                              {conv.firstUtmSource && (
                                <span className="text-[10px] text-white/20">
                                  {t("leadJourney.via")} {conv.firstUtmSource}
                                  {conv.firstUtmSource !== conv.lastUtmSource && conv.lastUtmSource
                                    ? ` → ${conv.lastUtmSource}`
                                    : ""}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ─── Meta Lead Form answers (survey questions) ─── */}
                  {(() => {
                    // Collect all metaLeadFields arrays across this lead's conversions
                    // (typically just one for a Meta Lead Ad import).
                    const formAnswerGroups = journey.conversions
                      .map((c) => ({
                        convId: c.id,
                        fields: (c.metadata?.metaLeadFields ?? []) as Array<{
                          name: string;
                          values: string[];
                        }>,
                        formName: (c.metadata?.metaFormName ?? null) as string | null,
                      }))
                      .filter((g) => Array.isArray(g.fields) && g.fields.length > 0);
                    if (formAnswerGroups.length === 0) return null;

                    // Meta sends field names + answers with underscores instead of spaces
                    // (e.g. "ILE_AKTUALNIE_GENERUJESZ_MIESIĘCZNEGO_PRZYCHODU?", "nie_mam_biznesu_online,_nigdzie_nie_pracuję").
                    // Humanize for display: strip leading "_", replace "_" with space,
                    // collapse whitespace, and sentence-case so it reads naturally.
                    const humanize = (s: string): string => {
                      const cleaned = String(s ?? "")
                        .replace(/_/g, " ")
                        .replace(/\s+/g, " ")
                        .trim();
                      if (!cleaned) return "";
                      return (
                        cleaned.charAt(0).toLocaleUpperCase("pl-PL") +
                        cleaned.slice(1).toLocaleLowerCase("pl-PL")
                      );
                    };
                    const humanizeQuestion = (name: string): string => {
                      // Keep "?" at end if present; trim trailing parens-only fragments like "(ZE..."
                      const h = humanize(name);
                      return h;
                    };
                    const humanizeAnswer = (values: string[]): string => {
                      if (!values || values.length === 0) return "";
                      return values.map((v) => humanize(v)).join(", ");
                    };

                    return (
                      <div className="bg-amber-500/[0.05] border border-amber-500/[0.15] rounded-lg p-4 space-y-3">
                        <h3 className="text-sm font-medium text-amber-300">
                          Odpowiedzi z formularza blyskawicznego
                        </h3>
                        {formAnswerGroups.map((group) => (
                          <div key={group.convId} className="space-y-3">
                            {group.formName && (
                              <div className="flex items-center gap-1.5 text-xs text-white/60">
                                <span className="text-white/40">Formularz:</span>
                                <span className="text-amber-200 font-medium">{group.formName}</span>
                              </div>
                            )}
                            <dl className="space-y-3">
                              {group.fields.map((f, idx) => {
                                const question = humanizeQuestion(f.name);
                                const answer = humanizeAnswer(f.values || []);
                                return (
                                  <div key={`${group.convId}-${idx}`} className="space-y-0.5">
                                    <dt
                                      className="text-[11px] text-amber-200/70 leading-snug"
                                      title={f.name}
                                    >
                                      {question}
                                    </dt>
                                    <dd className="text-sm text-white/90 leading-relaxed break-words">
                                      {answer || <span className="text-white/30">—</span>}
                                    </dd>
                                  </div>
                                );
                              })}
                            </dl>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* ─── Touchpoints Timeline ─── */}
                  {journey.touchpoints.length > 0 && (
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 space-y-2">
                      <h3 className="text-sm font-medium text-white/80">
                        {t("leadJourney.touchpoints")}
                      </h3>
                      <div className="space-y-1.5">
                        {journey.touchpoints.map((tp) => {
                          const channelInfo =
                            CHANNEL_INFO[tp.channel || "other"] || CHANNEL_INFO.other;
                          return (
                            <div key={tp.id} className="flex items-center gap-2 text-xs">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#ef3e29]" />
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${channelInfo.color}`}
                              >
                                {t(channelInfo.labelKey)}
                              </span>
                              {tp.source && <span className="text-white/50">{tp.source}</span>}
                              {tp.campaign && (
                                <span className="text-white/30 truncate max-w-[200px]">
                                  {tp.campaign}
                                </span>
                              )}
                              <span className="text-white/20 ml-auto">
                                {formatDate(tp.capturedAt, dateLocale)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ─── Sessions + Events Timeline ─── */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-white/80">
                      {t("leadJourney.sessionsAndEvents")}
                    </h3>
                    {journey.sessions.map((session) => {
                      const sessionEvents = eventsBySession.get(session.sessionId) || [];
                      const isExpanded = expandedSessions.has(session.sessionId);
                      const hasConversionEvents = sessionEvents.some(
                        (e) =>
                          [
                            "lead",
                            "booked_call",
                            "qualified_call",
                            "purchase",
                            "upsell",
                            "refund",
                          ].includes(e.eventName) ||
                          (e.eventName === "view_content" && e.metadata?.source === "everwebinar")
                      );

                      return (
                        <div
                          key={session.id}
                          className={`bg-white/[0.03] border rounded-lg overflow-hidden ${hasConversionEvents ? "border-emerald-500/20" : "border-white/[0.06]"}`}
                        >
                          {/* Session header */}
                          <button
                            className="w-full flex items-center gap-2 p-3 text-left hover:bg-white/[0.03] transition-colors"
                            onClick={() => toggleSession(session.sessionId)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-xs">
                                <Globe className="w-3 h-3 text-white/30 flex-shrink-0" />
                                <span
                                  className="text-white/60 break-all"
                                  title={session.landingPage ?? ""}
                                >
                                  {shortenUrl(session.landingPage)}
                                </span>
                                {session.utmSource && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">
                                    {session.utmSource}
                                    {session.utmCampaign ? ` / ${session.utmCampaign}` : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[10px] text-white/20">
                                {t("leadJourney.eventsCount", { count: sessionEvents.length })}
                              </span>
                              <span className="text-[10px] text-white/30">
                                {formatDate(session.startedAt, dateLocale)}
                              </span>
                            </div>
                          </button>

                          {/* Events list */}
                          {isExpanded && sessionEvents.length > 0 && (
                            <div className="border-t border-white/[0.04] px-3 py-2 space-y-1">
                              {sessionEvents.map((event) => {
                                const isWebinarEvent =
                                  event.eventName === "view_content" &&
                                  event.metadata?.source === "everwebinar";
                                const webinarEventLabelKey = isWebinarEvent
                                  ? event.metadata?.webinar_event === "attended"
                                    ? "leadJourney.webinar.live"
                                    : event.metadata?.webinar_event === "offer_reached"
                                      ? "leadJourney.webinar.offer"
                                      : "leadJourney.webinar.default"
                                  : null;
                                const info = isWebinarEvent
                                  ? {
                                      icon: Monitor,
                                      color: "text-purple-400",
                                      label: t(webinarEventLabelKey!),
                                    }
                                  : (() => {
                                      const cfg = EVENT_INFO[event.eventName];
                                      if (cfg)
                                        return {
                                          icon: cfg.icon,
                                          color: cfg.color,
                                          label: t(cfg.labelKey),
                                        };
                                      return {
                                        icon: Eye,
                                        color: "text-white/30",
                                        label: event.eventName,
                                      };
                                    })();
                                const Icon = info.icon;
                                return (
                                  <div
                                    key={event.id}
                                    className={`py-1 ${isWebinarEvent ? "bg-purple-500/[0.05] rounded px-1 -mx-1" : ""}`}
                                  >
                                    <div className="flex items-center gap-2 text-xs">
                                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                        <Icon className={`w-3 h-3 ${info.color}`} />
                                      </div>
                                      <span
                                        className={
                                          isWebinarEvent
                                            ? "text-purple-400 font-medium"
                                            : "text-white/60"
                                        }
                                      >
                                        {info.label}
                                      </span>
                                      {event.email && (
                                        <span className="text-white/30 truncate">
                                          {event.email}
                                        </span>
                                      )}
                                      {event.revenue && (
                                        <span className="text-emerald-400 font-medium">
                                          {Number(event.revenue).toLocaleString(dateLocale)}{" "}
                                          {event.currency || "PLN"}
                                        </span>
                                      )}
                                      <span className="text-white/20 ml-auto text-[10px] flex-shrink-0">
                                        {formatDate(event.serverTimestamp, dateLocale)}
                                      </span>
                                    </div>
                                    {event.pageUrl && (
                                      <a
                                        href={event.pageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block pl-6 mt-0.5 text-[10px] text-sky-400/60 hover:text-sky-300 break-all"
                                        title={event.pageUrl}
                                      >
                                        {shortenUrl(event.pageUrl)}
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {journey.sessions.length === 0 && journey.events.length > 0 && (
                      <div className="text-xs text-white/30 py-2">
                        {t("leadJourney.eventsNoSessions", { count: journey.events.length })}
                      </div>
                    )}
                  </div>

                  {/* ─── Identities ─── */}
                  {journey.identities.length > 0 && (
                    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 space-y-2">
                      <h3 className="text-sm font-medium text-white/80">
                        {t("leadJourney.identities")}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {journey.identities.map((id, i) => (
                          <span
                            key={i}
                            className="text-[10px] px-2 py-1 rounded bg-white/[0.05] text-white/50"
                          >
                            {id.identityType}: {id.identityValue}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
